import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, LogOut } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarSeparator,
  SidebarTrigger,
} from '@/components/ui/sidebar';

import { cn } from '@/lib/utils';

import { useIsMobile } from '@/hooks';

import { SidebarMenuProps } from '@/types';

import { useConfirmation } from '@/providers';

import { MobileSidebarHeader } from './MobileSidebarHeader';
import { SidebarNavItem } from './SidebarNavItem';

export const AppSideBar = (props: SidebarMenuProps) => {
  const { log_out_props, sections, active_section_key } = props;
  const activeSection =
    sections.find((section) => section.key === active_section_key) ??
    sections[0];
  const nav = activeSection?.nav ?? [];
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { confirm } = useConfirmation();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = contentRef.current;
    const activeEl = container?.querySelector('[data-nav-active]');
    if (!container || !activeEl) return;

    const scrollOffset = 24;
    const targetTop =
      container.scrollTop +
      activeEl.getBoundingClientRect().top -
      container.getBoundingClientRect().top -
      scrollOffset;

    container.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    });
  }, []);

  const onLogout = useCallback(() => {
    confirm({
      title: t('Are You Sure?'),
      description: '',
      confirmLabel: t('Log Out'),
      confirmVariant: 'destructive',
      onConfirm: () => {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = log_out_props?.url || '';

        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = 'delete';
        form.appendChild(methodInput);

        const tokenInput = document.createElement('input');
        tokenInput.type = 'hidden';
        tokenInput.name = 'authenticity_token';
        tokenInput.value = log_out_props?.token || '';
        form.appendChild(tokenInput);

        document.body.appendChild(form);
        form.submit();
      },
    });
  }, [confirm, log_out_props, t]);

  return (
    <Sidebar
      collapsible="icon"
      className="top-(--header-height) h-[calc(100svh-var(--header-height))]!"
    >
      <SidebarHeader
        className={cn(
          'p-2 pb-0',
          isMobile && 'dark bg-sidebar text-foreground',
        )}
      >
        {!isMobile && (
          <div className="flex items-center gap-2">
            <SidebarTrigger />
            <span className="text-sidebar-menu-foreground truncate text-sm font-semibold group-data-[collapsible=icon]:hidden">
              {activeSection ? t(activeSection.label) : null}
            </span>
          </div>
        )}
        {isMobile && (
          <MobileSidebarHeader
            {...props}
            activeSection={activeSection}
          />
        )}
      </SidebarHeader>
      <SidebarContent
        ref={contentRef}
        className="gap-1"
      >
        <SidebarGroup className="p-2">
          <SidebarMenu className="gap-0.5">
            {nav.map((item) => (
              <SidebarNavItem
                key={item.text + item.icon}
                item={item}
              />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="gap-1 p-2">
        <SidebarSeparator className="mx-0 my-0.5" />
        <SidebarMenuButton
          asChild
          tooltip={t('Documentation')}
          className="text-primary/70 hover:text-primary hover:bg-primary/10 h-8 gap-1.5 text-xs"
        >
          <a
            href="https://docs.google.com/document/d/1nd7aFK0dCXjWXjx0bD-Y2PjnHLxbmpETpLADVm-AqiQ/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-w-0 items-center gap-1.5"
          >
            <FileText className="size-4 shrink-0" />
            <span>v6.0.0</span>
          </a>
        </SidebarMenuButton>
        <SidebarMenuButton
          asChild
          tooltip={t('Log Out')}
          className="h-8 gap-1.5 text-xs"
        >
          <Button
            variant="ghost"
            className="bg-background text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex min-w-0 items-center justify-start gap-1.5 p-2! text-xs"
            onClick={onLogout}
          >
            <LogOut />
            <span>{t('Log Out')}</span>
          </Button>
        </SidebarMenuButton>
      </SidebarFooter>
    </Sidebar>
  );
};
